import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Availability, AvailabilityDaySourceAttribution } from '../types/availability';
import { AvailabilityMap } from '../utils/availabilityMap';
import { AvailabilityGapMap } from '../utils/availabilityGaps';
import { buildMonthGrid } from '../utils/calendarGrid';
import { formatMonthHeading, getWeekdayAbbreviations } from '../utils/calendarI18n';
import { CalendarDayCell } from './CalendarDayCell';

interface AvailabilityCalendarViewProps {
  readonly availabilities: readonly Availability[];
  readonly availabilityMap: AvailabilityMap;
  readonly availabilityGapMap?: AvailabilityGapMap;
  readonly defaultPrice: number;
  readonly daySourceAttribution?: ReadonlyMap<string, AvailabilityDaySourceAttribution>;
  readonly onEdit: (availability: Availability) => void;
  readonly onBlock: (id: string) => void;
  readonly onUnblock: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onAddFromDay: (isoDate: string) => void;
  readonly isMutating: boolean;
}

export const AvailabilityCalendarView = ({
  availabilities,
  availabilityMap,
  availabilityGapMap,
  daySourceAttribution,
  onEdit,
  onBlock,
  onUnblock,
  onRemove,
  onAddFromDay,
  isMutating,
}: AvailabilityCalendarViewProps) => {
  const { t, i18n } = useTranslation(['common', 'pages']);

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);

  const goToPrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear(y => y - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear(y => y + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const cells = buildMonthGrid(currentYear, currentMonth);
  const weekdays = getWeekdayAbbreviations(i18n.language);
  const heading = formatMonthHeading(currentYear, currentMonth, i18n.language);

  return (
    <div className="card">
      {/* Month navigation header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          aria-label={t('pages:previousMonth') as string}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        <h2 className="text-lg font-semibold text-gray-900">{heading}</h2>

        <button
          onClick={goToNextMonth}
          aria-label={t('pages:nextMonth') as string}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Empty state */}
      {availabilities.length === 0 && (
        <p className="text-center text-gray-500 text-sm mb-4">
          {t('pages:noAvailabilityPeriods')}
        </p>
      )}

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {weekdays.map(wd => (
          <div
            key={wd}
            role="columnheader"
            className="text-center text-xs font-medium text-gray-500 py-1"
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Day cells grid */}
      <div className="grid grid-cols-7">
        {cells.map(day => (
          <CalendarDayCell
            key={day.iso}
            day={day}
            availability={availabilityMap.get(day.iso)}
            gapMarker={availabilityGapMap?.get(day.iso)}
            daySourceAttribution={daySourceAttribution?.get(day.iso)}
            onEdit={onEdit}
            onBlock={onBlock}
            onUnblock={onUnblock}
            onRemove={onRemove}
            onAddFromDay={onAddFromDay}
            isMutating={isMutating}
          />
        ))}
      </div>
    </div>
  );
};
